import React, { useContext, useEffect, useState } from "react";
import { ChatbotContext } from "@/store/context/ChatbotContext.tsx";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/shadcn/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/shadcn/form";
import { Switch } from "@/components/shadcn/switch";
import { Input } from "@/components/shadcn/input";

import { useTranslation } from "react-i18next";
import { saveSettings } from "@/store/chatbot.ts";
import { useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/shadcn/card.tsx";
import {
  InputMultiSelect,
  InputMultiSelectTrigger,
} from "@/components/shadcn-custom/multiselect.tsx";
import { Textarea } from "@/components/shadcn/textarea.tsx";
import Loading from "@/components/Loading";

const catalogCategories = [
  "Учеба",
  "Работа с текстом",
  "Работа с данными",
  "Макретинг (SEO/SMM/Соцсети)",
  "IT и Разработка",
  "Идеи",
  "Документы",
  "Развлечения",
  "Для Бизнеса",
  "Лайфстайл",
  "Маркетплейсы",
  "Языки",
  "Коучинг",
  "Финансы",
  "Психология",
];

interface CategoryOption {
  value: string;
  label: string;
}

let options: any[] = [];
catalogCategories.forEach((item) => {
  options.push({
    value: item,
    label: item,
  });
});

const FormSchema = z.object({
  publish: z.boolean(),
  title: z.string().or(z.literal("")),
  description: z.string().or(z.literal("")),
  categories: z.array(z.string()),
});

export default function CatalogSettings() {
  const { chatbotID } = useParams();
  const { t } = useTranslation();
  const { chatSettings, viewChatbot, invalidateChatbot, setSettings } =
    useContext(ChatbotContext);

  const [selectCategories, setSelectCategories] = useState<CategoryOption[]>([])

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
  });

  function onSubmit(data: z.infer<typeof FormSchema>) {
    let set = Object.assign({}, chatSettings);

    set.catalog = {
      publish: data.publish,
      description: data.description,
      categories: data.categories,
    };
    set.name = data.title;

    saveSettings(chatbotID, set, invalidateChatbot);
  }

  useEffect(() => {
    if (chatSettings) {
      let chatbotCategories = chatSettings?.catalog?.categories
        ? chatSettings.catalog.categories
        : [];

      let options = chatbotCategories.concat(catalogCategories);
      options = options.filter((e, i, self) => i === self.indexOf(e));

      setSelectCategories(
        options.map((item) => {
          return {
            value: item,
            label: item,
          };
        }),
      );

      form.reset({
        publish: chatSettings?.catalog?.publish
          ? chatSettings.catalog.publish
          : false,
        title: chatSettings?.name,
        description: chatSettings?.catalog?.description
          ? chatSettings.catalog.description
          : "",
        categories: chatbotCategories,
      });
    }
  }, [chatSettings]);

  if (!viewChatbot || !chatSettings) {
    return <Loading />;
  }

  return (
    <Card className="">
      <CardHeader>
        <CardTitle>Настройки для каталога</CardTitle>
        <CardDescription></CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="w-full xl:w-2/3 space-y-6 pt-6"
          >
            <FormField
              control={form.control}
              name="publish"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                  <div className="space-y-0.5">
                    <FormLabel className="cursor-pointer">
                      Опубликовать
                    </FormLabel>
                    <FormDescription>
                      Опубликовать ИИ ассистент в каталоге
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Заголовок</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormDescription></FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Описание карточки в каталоге</FormLabel>
                  <FormControl>
                    <Textarea placeholder="" rows={6} {...field} />
                  </FormControl>
                  <FormDescription></FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="mt-4 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 ">
              Категории
            </div>

            <InputMultiSelect
              options={selectCategories}
              value={
                chatSettings?.catalog?.categories
                  ? chatSettings.catalog.categories
                  : []
              }
              onValueChange={(v) => {
                form.setValue("categories", v);
              }}
            >
              {(provided) => <InputMultiSelectTrigger {...provided} />}
            </InputMultiSelect>

            <Button type="submit">Save</Button>
          </form>
        </Form>
      </CardContent>
      <CardFooter className="flex justify-between"></CardFooter>
    </Card>
  );
}
